import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { ClinicAlert, ReorderSuggestion } from "@dentacore/shared-types";

import { listClinicAlerts, resolveClinicAlert } from "../api/alerts";
import { listReorder } from "../api/suppliers";
import { useAuth } from "../auth/auth-context";
import { useCan } from "../auth/use-can";
import { useModules } from "../shell/modules-context";
import { ReorderActions } from "../inventory/reorder-actions";
import { useLowStockSignal } from "../inventory/low-stock-watch";
import { RING_ICONS } from "../shell/ring/ring-icons";
import { useRingProvider } from "../shell/ring/ring-registry";
import { Modal } from "../ui/modal";
import styles from "./home.module.css";
import { Pager, fitArea, useFitPages } from "../ui/paging";

/** `busy` while every alert is being cleared. Not a UUID, so never an alert id. */
const ALL = "all";

/**
 * *Elsewhere in the clinic* — STORY-089.
 *
 * ── The mockup's row shape, the story's subject ─────────────────────────────
 *
 * The mockup draws a severity dot, a title, a muted detail on the same line, a
 * right-aligned `where`, and **a chevron only when there is somewhere to go**.
 * That shape is kept. What it is *about* changes: the mockup's four rows are
 * modules — Inventory, Equipment, Lab, Staff — and working plan §7 records that
 * only the last of those has a module. The story is about branches.
 *
 * ── A few rows, then a popup (client, 2026-08-28) ───────────────────────────
 *
 * The panel showed every unresolved alert, which on a busy clinic ran long
 * enough to push the rest of the dashboard off the screen. It now shows the
 * first few — worst first, since the list is already ordered that way — and a
 * button opens the full list in a dialog. **Still one list, never a filter
 * (E2)**: the popup holds all of them, and marking one done works the same in
 * either place.
 *
 * ── The dot is not the only signal ──────────────────────────────────────────
 *
 * Severity carries a **word as well as a colour**, for the same reason the
 * tooth chart carries shapes (STORY-023): a panel that says "this one is
 * urgent" only in red is a panel a colour-blind dentist cannot read.
 */
const INLINE_LIMIT = 3;

export function Elsewhere({
  branchId,
  inlineLimit = INLINE_LIMIT,
  onCount,
  bare = false,
}: {
  branchId: string | null;
  /**
   * How many rows to draw before *Show all*. Home's Elsewhere tab has a box of
   * its own that scrolls (CS2), so it shows more than the old three.
   */
  inlineLimit?: number;
  /** How many alerts are outstanding, whenever that changes — for the tab's count. */
  onCount?: (count: number) => void;
  /** Inside a tab that already names it: no card, no heading of its own. */
  bare?: boolean;
}): React.JSX.Element {
  const [alerts, setAlerts] = useState<readonly ClinicAlert[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);

  /*
   * Who to buy each low item from (STORY-171), keyed by the notice. Read for the
   * active location only — the order is placed there — and silently empty for
   * anyone who cannot see inventory, whose notices then read as before.
   */
  const [reorder, setReorder] = useState<ReadonlyMap<string, ReorderSuggestion>>(new Map());
  const [reorderError, setReorderError] = useState<string | null>(null);
  const can = useCan();
  // Order suggestions are Supply's (STORY-188d); the alerts themselves are base.
  const modules = useModules();
  const hasSupply = modules.states !== null && modules.writable("SUPPLY");
  const canSeeStock = can("inventory.view") && hasSupply;
  const canOrder = can("inventory.movement.record");
  // Reorder suggestions quote supplier prices: the home currency (QA4-D11).
  const currency = useAuth().user?.currency ?? "USD";
  // Bumped when anything low-stock changes elsewhere — an order, an arrival, a count.
  const signal = useLowStockSignal();

  const reload = useCallback(
    async (abort?: AbortSignal) => {
      setAlerts(await listClinicAlerts(abort));
      if (!canSeeStock || branchId === null) return;
      try {
        const list = await listReorder(abort);
        setReorder(new Map(list.map((s) => [s.alertId, s])));
      } catch {
        setReorder(new Map());
      }
    },
    [canSeeStock, branchId],
  );

  useEffect(() => {
    if (alerts !== null) onCount?.(alerts.length);
  }, [alerts, onCount]);

  useEffect(() => {
    const controller = new AbortController();

    reload(controller.signal).catch(() => {
      if (controller.signal.aborted) return;
      // Silent, and empty rather than an error: this is a secondary panel, and
      // a dashboard that cannot reach one endpoint should still show today's
      // list. The empty state below says there is nothing outstanding, which is
      // what a reader needs either way.
      setAlerts([]);
    });

    return () => controller.abort();
  }, [reload, signal]);

  function markDone(alertId: string): void {
    setBusy(alertId);
    resolveClinicAlert(alertId)
      .then((next) => {
        setAlerts(next);
        // Nothing left to overflow — close the popup rather than leaving an
        // empty dialog open.
        if (next.length <= inlineLimit) setShowAll(false);
      })
      .catch(() => {
        /* Leave the row; the next read settles it. */
      })
      .finally(() => setBusy(null));
  }

  /**
   * **Clear the lot** (client's manual QA, item 5).
   *
   * Marking fourteen rows done one at a time is fourteen clicks and fourteen
   * round trips, which is what the client was doing. Sent in parallel and the
   * list re-read once at the end — the endpoint returns the remaining alerts,
   * so racing them would leave whichever answered last on screen and that is
   * not necessarily the emptiest.
   *
   * **No confirmation.** Every one of these is individually reversible in the
   * sense that matters — an alert is a derived reminder, not a record, and the
   * next read raises it again if the underlying thing is still true. A dialog
   * guarding an action that cannot lose anything is friction.
   */
  function markAllDone(): void {
    /**
     * **Google notices are excluded** (STORY-130).
     *
     * The comment above justifies the absence of a confirmation on the grounds
     * that nothing here can be lost, because the next read raises it again.
     * That was the one claim `GOOGLE_UNCONFIRMED` did not honour — it was
     * raised at pairing and never again — so one click here used to end the
     * only signal that a practice's calendars never arrived.
     *
     * The sweep now re-raises it, so the claim is true again. It is still
     * excluded: this button clears *reminders*, and an unconfirmed calendar is
     * an unfinished step with its own button two lines up.
     */
    const ids = (alerts ?? [])
      .filter((alert) => alert.kind !== GOOGLE_UNCONFIRMED)
      .map((alert) => alert.id);
    if (ids.length === 0) return;

    setBusy(ALL);
    Promise.allSettled(ids.map((id) => resolveClinicAlert(id)))
      .then(() => reload())
      .then(() => {
        setShowAll(false);
      })
      .catch(() => {
        /* The next read settles it. */
      })
      .finally(() => {
        setBusy(null);
      });
  }

  const navigate = useNavigate();
  const isAdministrator = useAuth().user?.canAdminister === true;

  /**
   * **An alert, on the quick ring** (STORY-221, `home.alert`). Each action is
   * a button already on the row: *Order it* and *It arrived* are the low-stock
   * notice's own buttons, pressed where they are drawn so their dialogs open
   * exactly as on a click; *Mark done* is the row's, with the same rule about
   * critical notices; *Confirm* is a Google notice's in its place; *Open* is
   * the title's link.
   */
  useRingProvider("home.alert", (element) => {
    const alert = (alerts ?? []).find((row) => row.id === element.dataset.alertId);
    if (alert === undefined) return null;
    const low = alert.kind === INVENTORY_LOW;
    const suggestion = reorder.get(alert.id);
    const target = targetOf(alert);
    /** One of the notice's own buttons, inside this row — pressed, not imitated. */
    const press = (find: (button: HTMLButtonElement) => boolean) => () => {
      const button = [...element.querySelectorAll<HTMLButtonElement>("button")].find(find);
      if (button !== undefined && !button.disabled) button.click();
    };
    return {
      ring: "home.alert",
      title: alert.title,
      meta: [SEVERITY_WORD[alert.severity], alert.detail]
        .filter((part): part is string => part !== null)
        .join(" · "),
      actions: [
        low &&
          suggestion !== undefined &&
          suggestion.openOrder === null &&
          suggestion.prices.length > 0 && {
            key: "order",
            label: "Order it",
            icon: RING_ICONS.cart,
            needs: ["inventory.movement.record"],
            module: "SUPPLY",
            hint: "Opens the order for its supplier",
            run: press((button) => button.dataset.testid === "reorder-order"),
          },
        low &&
          suggestion !== undefined &&
          suggestion.openOrder !== null && {
            key: "arrived",
            label: "It arrived",
            icon: RING_ICONS.truck,
            needs: ["inventory.movement.record"],
            module: "SUPPLY",
            hint: "Puts the delivery on the shelf",
            run: press((button) => button.textContent.trim() === "It arrived"),
          },
        alert.kind === GOOGLE_UNCONFIRMED
          ? {
              key: "confirm",
              label: "Confirm",
              icon: RING_ICONS.check,
              hint: "Confirms this calendar in System",
              run: () => navigate("/settings/google"),
            }
          : (alert.severity !== "CRITICAL" || isAdministrator) && {
              key: "done",
              label: "Mark done",
              icon: RING_ICONS.check,
              hint: "Clears this alert",
              run: () => markDone(alert.id),
            },
        target !== null &&
          (low
            ? {
                key: "open",
                label: "Open item",
                icon: RING_ICONS.box,
                needs: ["inventory.view"],
                module: "SUPPLY",
                hint: "Opens it in Inventory",
                run: () => navigate(target),
              }
            : {
                key: "open",
                label: "Open",
                icon: RING_ICONS.file,
                hint: "Goes where this alert points",
                run: () => navigate(target),
              }),
      ],
    };
  });

  const rows = alerts ?? [];
  // In Home's tab the list pages to the room it has; on its own it keeps the
  // first few and a "Show all".
  const paging = useFitPages({ keys: rows.map((row) => row.id), estimate: () => 100, gap: 0 });
  const [from, to] = paging.range;
  const inline = bare ? rows.slice(from, to) : rows.slice(0, inlineLimit);
  // Paged on Home, and still a "Show all" for the whole list at once — a
  // simpler screen never loses a function (CS1).
  const overflow = bare
    ? paging.pages.length > 1
      ? rows.length - inline.length
      : 0
    : rows.length - inlineLimit;

  return (
    <section
      className={bare ? `${styles.tabBody} ${styles.tabFill}` : styles.panel}
      data-testid="elsewhere"
    >
      {!bare && (
        <div className={styles.panelHead}>
          <h2 className={styles.panelTitle}>Elsewhere in the clinic</h2>
          <span className={styles.panelNote}>
            {alerts === null
              ? " "
              : rows.length === 0
                ? "nothing outstanding"
                : `${rows.length} to note`}
          </span>
        </div>
      )}

      {alerts === null ? (
        <p className={styles.quiet}>Reading&hellip;</p>
      ) : rows.length === 0 ? (
        <p className={styles.blank} data-testid="elsewhere-empty">
          Nothing outstanding anywhere in the clinic.
        </p>
      ) : (
        <>
          <div
            className={bare ? `${fitArea} ${styles.fillArea}` : undefined}
            ref={bare ? paging.areaRef : undefined}
          >
            <ul className={styles.rows}>
              {inline.map((alert) => (
                <AlertRow
                  rowRef={bare ? paging.measure(alert.id) : undefined}
                  key={alert.id}
                  alert={alert}
                  branchId={branchId}
                  busy={busy === alert.id}
                  onDone={() => markDone(alert.id)}
                  reorder={
                    reorder.get(alert.id) === undefined ? undefined : (
                      <ReorderActions
                        suggestion={reorder.get(alert.id) as ReorderSuggestion}
                        currency={currency}
                        canOrder={canOrder}
                        onChanged={() => {
                          setReorderError(null);
                          void reload();
                        }}
                        onError={setReorderError}
                      />
                    )
                  }
                />
              ))}
            </ul>
          </div>
          {bare && (
            <Pager
              compact
              page={paging.page}
              count={paging.pages.length}
              onPage={paging.setPage}
              from={from + 1}
              to={to}
              total={rows.length}
              noun="alerts"
              testId="elsewhere-pager"
            />
          )}

          {reorderError !== null && (
            <p className={styles.quiet} role="alert">
              {reorderError}
            </p>
          )}

          {overflow > 0 && (
            <button
              type="button"
              className={styles.showAll}
              onClick={() => setShowAll(true)}
              data-testid="elsewhere-show-all"
            >
              Show all {rows.length}
            </button>
          )}

          {rows.length > 1 && (
            <button
              type="button"
              className={styles.showAll}
              onClick={markAllDone}
              disabled={busy !== null}
              data-testid="elsewhere-mark-all"
            >
              {busy === ALL ? "Clearing…" : "Mark all as done"}
            </button>
          )}
        </>
      )}

      {showAll && (
        <Modal
          label="Everything outstanding in the clinic"
          testId="elsewhere-dialog"
          panelClassName={styles.dialog}
          onClose={() => {
            setShowAll(false);
          }}
        >
          <div className={styles.dialogHead}>
            <h3 className={styles.panelTitle}>Elsewhere in the clinic</h3>
            <div className={styles.dialogActions}>
              {/*
                Here as well as on the panel (item 5) — this is the list with
                fourteen rows in it, so it is where clearing them all is
                actually wanted.
              */}
              <button
                type="button"
                className={styles.dialogClose}
                onClick={markAllDone}
                disabled={busy !== null || rows.length === 0}
                data-testid="elsewhere-dialog-mark-all"
              >
                {busy === ALL ? "Clearing…" : "Mark all as done"}
              </button>
              <button
                type="button"
                className={styles.dialogClose}
                onClick={() => setShowAll(false)}
                data-testid="elsewhere-dialog-close"
              >
                Close
              </button>
            </div>
          </div>
          <ul className={`${styles.rows} ${styles.dialogRows}`}>
            {rows.map((alert) => (
              <AlertRow
                key={alert.id}
                alert={alert}
                branchId={branchId}
                busy={busy === alert.id}
                onDone={() => markDone(alert.id)}
                reorder={
                  reorder.get(alert.id) === undefined ? undefined : (
                    <ReorderActions
                      suggestion={reorder.get(alert.id) as ReorderSuggestion}
                      currency={currency}
                      canOrder={canOrder}
                      onChanged={() => {
                        setReorderError(null);
                        void reload();
                      }}
                      onError={setReorderError}
                    />
                  )
                }
              />
            ))}
          </ul>
        </Modal>
      )}
    </section>
  );
}

function AlertRow({
  alert,
  branchId,
  busy,
  onDone,
  reorder,
  rowRef,
}: {
  /** Home's pager measures the first row it draws. */
  rowRef?: ((element: HTMLElement | null) => (() => void) | undefined) | undefined;
  alert: ClinicAlert;
  branchId: string | null;
  busy: boolean;
  onDone: () => void;
  /** A low-stock notice's recommendation and *Order it* (STORY-171). */
  reorder?: React.ReactNode;
}): React.JSX.Element {
  const isAdministrator = useAuth().user?.canAdminister === true;
  const target = targetOf(alert);
  return (
    <li
      ref={rowRef}
      className={styles.alertRow}
      data-testid="elsewhere-row"
      data-alert-id={alert.id}
      // An alert, to the quick ring (STORY-221).
      data-ring="home.alert"
    >
      <span className={`${styles.alertDot} ${dotClassOf(alert.severity)}`} aria-hidden="true" />

      <span className={styles.alertBody}>
        <span className={styles.alertTitle}>
          {target === null ? (
            alert.title
          ) : (
            <Link to={target} className={styles.alertLink}>
              {alert.title}
              <span className={styles.chevron} aria-hidden="true">
                {" "}
                ›
              </span>
            </Link>
          )}
        </span>
        {alert.detail !== null && <span className={styles.alertDetail}>{alert.detail}</span>}
        {/* Who is on it (OQ-D12): the Low stock task this alert raised. */}
        {alert.taskId !== null && (
          <Link
            to={"/tasks?task=" + encodeURIComponent(alert.taskId)}
            className={styles.alertLink}
            data-testid="elsewhere-task"
          >
            See the task
          </Link>
        )}
        {reorder !== undefined && <span className={styles.alertReorder}>{reorder}</span>}

        {/*
          The severity word, so the dot is never the only carrier — and the
          branch name only when it is somewhere other than here.

          **Under the title rather than beside it.** This panel lives in a
          ~430px rail, and as a third column this pair was `flex: none` with
          `white-space: nowrap`, so a name like "Branch Westgate — TEST" took
          its width out of the title's. The title then wrapped to two lines and
          the detail truncated mid-word ("below the reorder threshol…"). On its
          own line the pair costs nothing horizontally, and the title and detail
          get the full width of the row.
        */}
        <span className={styles.alertWhere}>
          <span className={styles.alertSeverity} data-severity={alert.severity}>
            {SEVERITY_WORD[alert.severity]}
          </span>
          {alert.branchName !== null && alert.branchId !== branchId && (
            <span className={styles.alertBranch} data-testid="elsewhere-branch">
              {alert.branchName}
            </span>
          )}
        </span>
      </span>

      {/*
        **A Google notice cannot be marked done — it is fixed, not dismissed**
        (STORY-130).

        Every other kind here is a derived reminder: clear it and the next read
        raises it again if it is still true. `GOOGLE_UNCONFIRMED` is now derived
        too — the daily sweep re-raises it — but a day is a long time to be
        silent about calendars that never arrived, and "done" is the wrong word
        for a step nobody has taken. Confirming in Settings is what resolves it,
        so the button goes there instead of pretending to.
      */}
      {alert.kind === GOOGLE_UNCONFIRMED ? (
        <Link
          to="/settings/google"
          className={styles.alertDone}
          data-testid="elsewhere-google-fix"
          aria-label="Confirm this calendar in System"
        >
          Confirm
        </Link>
      ) : alert.severity === "CRITICAL" && !isAdministrator ? null : (
        /* A critical notice needs an administrator to mark it done (QF-D10). */
        <button
          type="button"
          className={styles.alertDone}
          disabled={busy}
          onClick={onDone}
          data-testid="elsewhere-done"
        >
          Mark done
        </button>
      )}
    </li>
  );
}

/**
 * The one alert kind this panel does not offer to dismiss.
 *
 * Kept as a literal rather than imported from the API: `apps/web` does not
 * depend on `apps/api`, and the string is the contract between them — the same
 * way the web side already names statuses and roles.
 */
const GOOGLE_UNCONFIRMED = "GOOGLE_UNCONFIRMED";

/** STORY-112's kind, named here for the same reason as the one above. */
const INVENTORY_LOW = "INVENTORY_LOW";

/**
 * Where an alert's title goes. A low-stock notice stores the bare item id as
 * its target (STORY-112), which as a route was "page not found". It opens the
 * item on Inventory instead.
 */
function targetOf(alert: ClinicAlert): string | null {
  if (alert.linkTarget === null) return null;
  return alert.kind === INVENTORY_LOW
    ? `/inventory?item=${encodeURIComponent(alert.linkTarget)}`
    : alert.linkTarget;
}

/** A word, not only a colour — see the docblock above. */
const SEVERITY_WORD: Record<ClinicAlert["severity"], string> = {
  CRITICAL: "Urgent",
  WARNING: "Attention",
  INFO: "Note",
};

function dotClassOf(severity: ClinicAlert["severity"]): string {
  if (severity === "CRITICAL") return styles.alertDotCritical ?? "";
  if (severity === "WARNING") return styles.alertDotWarning ?? "";
  return styles.alertDotInfo ?? "";
}
